import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Building, Plus, Edit, Trash2, CreditCard, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminCabinets = () => {
  const { token } = useAuth();
  const [cabinets, setCabinets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCabinet, setEditingCabinet] = useState(null);
  const [formData, setFormData] = useState({
    nom: '', adresse: '', code_postal: '', ville: '', siret: '', tva: '', iban: '', bic: '', email: '', telephone: ''
  });

  useEffect(() => { fetchCabinets(); }, [token]);

  const fetchCabinets = async () => {
    try {
      const response = await axios.get(`${API}/cabinets`, { headers: { Authorization: `Bearer ${token}` } });
      setCabinets(response.data);
    } catch (error) {
      console.error('Error fetching cabinets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCabinet) {
        await axios.put(`${API}/cabinets/${editingCabinet.id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/cabinets`, formData, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowModal(false);
      setEditingCabinet(null);
      setFormData({ nom: '', adresse: '', code_postal: '', ville: '', siret: '', tva: '', iban: '', bic: '', email: '', telephone: '' });
      fetchCabinets();
    } catch (error) {
      console.error('Error saving cabinet:', error);
    }
  };

  const handleEdit = (cabinet) => {
    setEditingCabinet(cabinet);
    setFormData({
      nom: cabinet.nom || '', adresse: cabinet.adresse || '', code_postal: cabinet.code_postal || '',
      ville: cabinet.ville || '', siret: cabinet.siret || '', tva: cabinet.tva || '',
      iban: cabinet.iban || '', bic: cabinet.bic || '', email: cabinet.email || '', telephone: cabinet.telephone || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce cabinet ?')) {
      try {
        await axios.delete(`${API}/cabinets/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        fetchCabinets();
      } catch (error) {
        console.error('Error deleting cabinet:', error);
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-cabinets-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">Gestion des Cabinets</h1>
        <button onClick={() => { setEditingCabinet(null); setFormData({ nom: '', adresse: '', code_postal: '', ville: '', siret: '', tva: '', iban: '', bic: '', email: '', telephone: '' }); setShowModal(true); }}
          className="bg-[#00D3A8] hover:bg-[#00C7A1] text-white py-2 px-4 rounded-lg flex items-center space-x-2">
          <Plus size={20} /><span>Nouveau cabinet</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cabinet</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SIRET</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {cabinets.map((cabinet) => (
              <tr key={cabinet.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-[#00D3A8]/10 rounded-lg flex items-center justify-center">
                      <Building size={20} className="text-[#00D3A8]" />
                    </div>
                    <span className="font-medium text-gray-800">{cabinet.nom}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{cabinet.adresse}<br/>{cabinet.code_postal} {cabinet.ville}</td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {cabinet.siret && <div className="flex items-center"><FileText size={14} className="mr-1" />{cabinet.siret}</div>}
                  {cabinet.tva && <div className="text-xs text-gray-400 mt-1">TVA: {cabinet.tva}</div>}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{cabinet.email}<br/>{cabinet.telephone}</td>
                <td className="px-4 py-3">
                  <div className="flex space-x-2">
                    <button onClick={() => handleEdit(cabinet)} className="text-gray-400 hover:text-[#00D3A8]"><Edit size={18} /></button>
                    <button onClick={() => handleDelete(cabinet.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={18} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {cabinets.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Building size={48} className="mx-auto mb-4 text-gray-300" />
            <p>Aucun cabinet</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">{editingCabinet ? 'Modifier' : 'Nouveau'} cabinet</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du cabinet</label>
                <input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                <input type="text" value={formData.adresse} onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code postal</label>
                  <input type="text" value={formData.code_postal} onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville</label>
                  <input type="text" value={formData.ville} onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-medium text-gray-800 mb-3 flex items-center"><FileText size={16} className="mr-2" />Informations légales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
                    <input type="text" value={formData.siret} onChange={(e) => setFormData({...formData, siret: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="123 456 789 00012" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° TVA</label>
                    <input type="text" value={formData.tva} onChange={(e) => setFormData({...formData, tva: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="FR12345678901" />
                  </div>
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <h3 className="font-medium text-gray-800 mb-3 flex items-center"><CreditCard size={16} className="mr-2" />Coordonnées bancaires</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IBAN</label>
                    <input type="text" value={formData.iban} onChange={(e) => setFormData({...formData, iban: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="FR76 1234 5678 9012 3456 7890 123" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">BIC</label>
                    <input type="text" value={formData.bic} onChange={(e) => setFormData({...formData, bic: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="BNPAFRPP" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
                <button type="submit" className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1]">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCabinets;
